import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsBookmarkOutlinedIcon from '@mui/icons-material/CollectionsBookmarkOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';
import ToggleButton, { ToggleButtonProps } from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import React, { useEffect, useState } from 'react';

import { themeColors } from '@/config/theme/themeColors.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { useGetProcessMultiOffer } from '@/views/processes/multiOffer/util/useGetProcessMultiOffer.util.ts';
import { useProcessOfferContext } from '@/views/processes/offer/context/ProcessOffer.context.tsx';
import { isPackageOffer } from '@/views/processes/offer/util/packagePricing.util.ts';

import { resolveOfferPrintTemplateTarget } from './offerPrintTemplate.util.ts';
import { ProcessOfferPrintTemplatePicker } from './ProcessOfferPrintTemplatePicker.comp.tsx';
import {
  OFFER_PRINT_TEMPLATE_MISSING,
  PdfOfferScope,
  PdfPreviewType,
  useProcessOfferPdfPreview,
} from './useProcessOfferPdfPreview.util.ts';

const TooltipToggleButton = React.forwardRef<
  HTMLButtonElement,
  ToggleButtonProps & { tooltip: React.ReactNode }
>(({ children, tooltip, ...props }, ref) => (
  <Tooltip title={tooltip}>
    <ToggleButton ref={ref} {...props}>
      {children}
    </ToggleButton>
  </Tooltip>
));
TooltipToggleButton.displayName = 'TooltipToggleButton';

const renderModeTooltip = (name: string, description: string): React.ReactNode => (
  <Box sx={{ py: 0.25 }}>
    <Box sx={{ fontWeight: 600 }}>{name}</Box>
    <Box sx={{ fontSize: 12, mt: 0.25, opacity: 0.9 }}>{description}</Box>
  </Box>
);

const LegendRow: React.FC<{ description: string; icon: React.ReactNode; name: string }> = ({
  description,
  icon,
  name,
}) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
    <Box sx={{ color: themeColors.greyDarker, display: 'flex', mt: '2px' }}>{icon}</Box>
    <Box>
      <Typography component="div" variant="f14Medium">
        {name}
      </Typography>
      <Typography component="div" sx={{ color: themeColors.greyDark }} variant="f14Regular">
        {description}
      </Typography>
    </Box>
  </Stack>
);

type Props = {
  multiProcessOfferGroupId: string;
  onClose: () => void;
  open: boolean;
  processId: string;
  processOfferId: string;
  sx?: SxProps<Theme>;
};

export const ProcessOfferPdfPreviewDrawer: React.FC<Props> = ({
  multiProcessOfferGroupId,
  onClose,
  open,
  processId,
  processOfferId,
  sx,
}) => {
  const { t } = useTranslationWithPrefix('views.process.offer.pdfPreview');
  const [previewType, setPreviewType] = useState<PdfPreviewType>('realization');
  const [offerScope, setOfferScope] = useState<PdfOfferScope>('current');
  const [legendAnchor, setLegendAnchor] = useState<HTMLElement | null>(null);

  const { error, isLoading, loadPdf, pdfUrl } = useProcessOfferPdfPreview({
    multiProcessOfferGroupId,
    processId,
    processOfferId,
  });
  const { processOffer } = useProcessOfferContext();
  const isPackage = processOffer ? isPackageOffer(processOffer) : false;

  const { isGetting: isGettingMultiOffer, processMultiOffer } = useGetProcessMultiOffer({
    multiOfferId: multiProcessOfferGroupId,
    processId,
  });

  const usesPrintTemplate = previewType !== 'realization';
  const templateTarget = resolveOfferPrintTemplateTarget(
    offerScope,
    processOfferId,
    processMultiOffer,
  );
  const isTemplateMissing = error?.code === OFFER_PRINT_TEMPLATE_MISSING;

  const scopeModes = [
    {
      description: t('scope.currentDesc'),
      icon: <VisibilityOutlinedIcon fontSize="small" />,
      name: t('scope.current'),
    },
    {
      description: t('scope.allDesc'),
      icon: <CollectionsBookmarkOutlinedIcon fontSize="small" />,
      name: t('scope.all'),
    },
  ];
  const typeModes = [
    {
      description: t('realizationDesc'),
      icon: <RestaurantOutlinedIcon fontSize="small" />,
      name: t('realization'),
    },
    {
      description: t('offerDesc'),
      icon: <DescriptionOutlinedIcon fontSize="small" />,
      name: t('offer'),
    },
    ...(isPackage
      ? [
          {
            description: t('offerSelectionDesc'),
            icon: <ChecklistOutlinedIcon fontSize="small" />,
            name: t('offerSelection'),
          },
          {
            description: t('offerSelectedDesc'),
            icon: <DoneAllOutlinedIcon fontSize="small" />,
            name: t('offerSelected'),
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (open && processOfferId) {
      void loadPdf(previewType, offerScope);
    }
  }, [open, previewType, offerScope, processOfferId]);

  const handleToggle = (_: React.MouseEvent, value: PdfPreviewType | null) => {
    if (value) {
      setPreviewType(value);
    }
  };

  const handleScopeToggle = (_: React.MouseEvent, value: PdfOfferScope | null) => {
    if (value) {
      setOfferScope(value);
    }
  };

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: { width: '50vw' },
        },
      }}
      sx={sx}
    >
      <Stack spacing={0} sx={{ height: '100%' }}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            borderBottom: `1px solid ${themeColors.stroke}`,
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 600 }} variant="f16Medium">
            {t('title')}
          </Typography>
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            {previewType !== 'realization' && (
              <ToggleButtonGroup
                exclusive
                onChange={handleScopeToggle}
                size="small"
                value={offerScope}
              >
                <TooltipToggleButton
                  tooltip={renderModeTooltip(t('scope.current'), t('scope.currentDesc'))}
                  value="current"
                >
                  <VisibilityOutlinedIcon fontSize="small" />
                </TooltipToggleButton>
                <TooltipToggleButton
                  tooltip={renderModeTooltip(t('scope.all'), t('scope.allDesc'))}
                  value="all"
                >
                  <CollectionsBookmarkOutlinedIcon fontSize="small" />
                </TooltipToggleButton>
              </ToggleButtonGroup>
            )}
            <ToggleButtonGroup exclusive onChange={handleToggle} size="small" value={previewType}>
              <TooltipToggleButton
                tooltip={renderModeTooltip(t('realization'), t('realizationDesc'))}
                value="realization"
              >
                <RestaurantOutlinedIcon fontSize="small" />
              </TooltipToggleButton>
              <TooltipToggleButton
                tooltip={renderModeTooltip(t('offer'), t('offerDesc'))}
                value="offer"
              >
                <DescriptionOutlinedIcon fontSize="small" />
              </TooltipToggleButton>
              {isPackage && (
                <TooltipToggleButton
                  tooltip={renderModeTooltip(t('offerSelection'), t('offerSelectionDesc'))}
                  value="offerSelection"
                >
                  <ChecklistOutlinedIcon fontSize="small" />
                </TooltipToggleButton>
              )}
              {isPackage && (
                <TooltipToggleButton
                  tooltip={renderModeTooltip(t('offerSelected'), t('offerSelectedDesc'))}
                  value="offerSelected"
                >
                  <DoneAllOutlinedIcon fontSize="small" />
                </TooltipToggleButton>
              )}
            </ToggleButtonGroup>
            <Tooltip title={t('legendTitle')}>
              <IconButton onClick={event => setLegendAnchor(event.currentTarget)} size="small">
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
            <Popover
              anchorEl={legendAnchor}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              onClose={() => setLegendAnchor(null)}
              open={Boolean(legendAnchor)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
              <Box sx={{ maxWidth: 360, p: 2 }}>
                <Typography component="div" sx={{ mb: 1.5 }} variant="f16Medium">
                  {t('legendTitle')}
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    color: themeColors.greyDark,
                    letterSpacing: 0.4,
                    mb: 0.75,
                    textTransform: 'uppercase',
                  }}
                  variant="f12Regular"
                >
                  {t('scopeLabel')}
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {scopeModes.map(mode => (
                    <LegendRow
                      description={mode.description}
                      icon={mode.icon}
                      key={mode.name}
                      name={mode.name}
                    />
                  ))}
                </Stack>
                <Typography
                  component="div"
                  sx={{
                    color: themeColors.greyDark,
                    letterSpacing: 0.4,
                    mb: 0.75,
                    textTransform: 'uppercase',
                  }}
                  variant="f12Regular"
                >
                  {t('typeLabel')}
                </Typography>
                <Stack spacing={1}>
                  {typeModes.map(mode => (
                    <LegendRow
                      description={mode.description}
                      icon={mode.icon}
                      key={mode.name}
                      name={mode.name}
                    />
                  ))}
                </Stack>
              </Box>
            </Popover>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
        {usesPrintTemplate && !isTemplateMissing && !isGettingMultiOffer && (
          <ProcessOfferPrintTemplatePicker
            key={`${templateTarget.level}-${templateTarget.offerPrintId ?? 'none'}`}
            multiProcessOfferGroupId={multiProcessOfferGroupId}
            onAssigned={() => loadPdf(previewType, offerScope)}
            processId={processId}
            target={templateTarget}
            variant="assigned"
          />
        )}
        <Box sx={{ flex: 1, position: 'relative' }}>
          {isLoading && (
            <Stack
              sx={{
                alignItems: 'center',
                height: '100%',
                inset: 0,
                justifyContent: 'center',
                position: 'absolute',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Stack>
          )}
          {!isLoading && isTemplateMissing && (
            <ProcessOfferPrintTemplatePicker
              key={`${templateTarget.level}-${templateTarget.offerPrintId ?? 'none'}`}
              multiProcessOfferGroupId={multiProcessOfferGroupId}
              onAssigned={() => loadPdf(previewType, offerScope)}
              processId={processId}
              target={templateTarget}
              variant="missing"
            />
          )}
          {!isLoading && error && !isTemplateMissing && (
            <Stack sx={{ alignItems: 'center', height: '100%', justifyContent: 'center', p: 4 }}>
              <Alert severity="error" sx={{ maxWidth: 520, width: '100%' }}>
                {error.message ?? t('genericError')}
              </Alert>
            </Stack>
          )}
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              style={{ border: 'none', height: '100%', width: '100%' }}
              title={t('title')}
            />
          )}
        </Box>
      </Stack>
    </Drawer>
  );
};
